import React, { useState, useEffect } from 'react';
import { Store, Package, Tag, X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
export const GALLERY_CATEGORIES = ['LOCATION', 'PRODUCTS', 'PRICE_LIST'];
export const CATEGORY_META = {
  LOCATION: { icon: Store, labelKey: 'gal_cat_locale' },
  PRODUCTS: { icon: Package, labelKey: 'gal_cat_prodotti' },
  PRICE_LIST: { icon: Tag, labelKey: 'gal_cat_listino' },
};

export const buildImgUrl = (relPath) => relPath?.startsWith('/api/') ? `${API}${relPath}` : relPath;

/** Compact chip filter row. Tutte + one chip per non-empty category. Optional count. */
export function GalleryFilterChips({ gallery, active, onChange, t, showCounts = true }) {
  const counts = { ALL: gallery.length };
  GALLERY_CATEGORIES.forEach(c => { counts[c] = gallery.filter(g => g.category === c).length; });
  const available = GALLERY_CATEGORIES.filter(c => counts[c] > 0);
  if (gallery.length === 0) return null;
  const btn = (key, label, Icon) => {
    const isActive = active === key;
    return (
      <button key={key} type="button" onClick={() => onChange(key)} data-testid={`gal-chip-${key.toLowerCase()}`}
        className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-sans font-medium transition-colors border ${isActive ? 'bg-primary text-white border-primary' : 'bg-card text-muted-foreground border-border hover:text-foreground'}`}>
        {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />}
        {label}
        {showCounts && <span className={`text-[10px] ${isActive ? 'opacity-90' : 'opacity-60'}`}>({counts[key]})</span>}
      </button>
    );
  };
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-none" data-testid="gallery-filter-chips">
      {btn('ALL', t('gal_all'))}
      {available.map(c => btn(c, t(CATEGORY_META[c].labelKey), CATEGORY_META[c].icon))}
    </div>
  );
}

/** Full-screen lightbox with keyboard nav + pinch/wheel zoom. */
export function GalleryLightbox({ images, index, onClose, onIndex }) {
  const [zoom, setZoom] = useState(1);
  useEffect(() => { setZoom(1); }, [index]);
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onIndex((index + 1) % images.length);
      if (e.key === 'ArrowLeft') onIndex((index - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', h); document.body.style.overflow = ''; };
  }, [index, images.length, onClose, onIndex]);
  if (!images[index]) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={onClose} data-testid="gallery-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 p-2 text-white/80 hover:text-white z-10" aria-label="Chiudi"><X className="w-6 h-6" /></button>
      {images.length > 1 && (<>
        <button onClick={(e)=>{e.stopPropagation();onIndex((index-1+images.length)%images.length);}} className="absolute left-2 sm:left-6 p-2 text-white/80 hover:text-white z-10" aria-label="Precedente"><ChevronLeft className="w-8 h-8" /></button>
        <button onClick={(e)=>{e.stopPropagation();onIndex((index+1)%images.length);}} className="absolute right-2 sm:right-6 p-2 text-white/80 hover:text-white z-10" aria-label="Successiva"><ChevronRight className="w-8 h-8" /></button>
      </>)}
      <div className="relative max-w-[95vw] max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}
           onWheel={e => { e.preventDefault(); setZoom(z => Math.max(1, Math.min(4, z + (e.deltaY < 0 ? 0.2 : -0.2)))); }}>
        <img src={buildImgUrl(images[index].url_full)} alt="" style={{transform:`scale(${zoom})`,transition:'transform 0.15s'}} className="max-w-[95vw] max-h-[90vh] object-contain select-none cursor-zoom-in" onClick={() => setZoom(z => z === 1 ? 2 : 1)} data-testid="lightbox-image" />
      </div>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-xs font-sans">{index + 1} / {images.length}</div>
    </div>
  );
}

/** Public gallery viewer with filter + main image + thumbs row. */
export function GalleryViewer({ gallery, t }) {
  const [filter, setFilter] = useState('ALL');
  const [lightbox, setLightbox] = useState(null);
  const filtered = filter === 'ALL' ? gallery : gallery.filter(g => g.category === filter);
  if (!gallery || gallery.length === 0) {
    return (
      <div className="w-full aspect-[16/9] rounded-2xl bg-muted/40 border border-border flex flex-col items-center justify-center text-muted-foreground" data-testid="gallery-empty">
        <Store className="w-10 h-10 mb-2" strokeWidth={1.2} />
        <p className="text-sm font-sans">{t('gal_no_photos')}</p>
      </div>
    );
  }
  const main = filtered[0];
  const rest = filtered.slice(1);
  return (
    <div className="space-y-3" data-testid="gallery-viewer">
      <GalleryFilterChips gallery={gallery} active={filter} onChange={setFilter} t={t} />
      {main && (
        <button onClick={() => setLightbox(0)} className="block w-full group relative overflow-hidden rounded-2xl border border-border" data-testid="gallery-main-image">
          <img src={buildImgUrl(main.url_display)} alt="" className="w-full aspect-[16/9] object-cover" loading="eager" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
            <Maximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
          </div>
        </button>
      )}
      {rest.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="gallery-thumbs">
          {rest.map((img, i) => (
            <button key={img.image_id} onClick={() => setLightbox(i + 1)} className="relative aspect-square rounded-lg overflow-hidden border border-border group" data-testid={`gallery-thumb-${i}`}>
              <img src={buildImgUrl(img.url_thumb)} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
            </button>
          ))}
        </div>
      )}
      {lightbox !== null && <GalleryLightbox images={filtered} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} />}
    </div>
  );
}
